import { patientApi } from "@mgdx/api/lib/patientApi";
import { pharmacyChainApi } from "@mgdx/api/lib/pharmacyChainApi";
import appName from "@mgdx/shared/src/appName";
import type { Patient } from "@mgdx/shared/src/models/Patient";
import { getApiError } from "@mgdx-libs/error-handler";

import { isSamePatient, startUpdatingPatient } from "../../hooks/patient/currentPatientQuery";
import { setCurrentUser } from "../../stores/currentUserStore";

type MccmExternalIdsV2InChain = {
  current_app_user_id?: string;
  ems_user_id?: unknown;
};

type MccmExternalIdsV2 = Record<string, MccmExternalIdsV2InChain | undefined>;

const getMccmExternalIdsV2 = (patient: Patient): MccmExternalIdsV2 | undefined => {
  if (!patient.externalIdsV2 || typeof patient.externalIdsV2 !== "object") return undefined;

  return patient.externalIdsV2 as MccmExternalIdsV2;
};

export const getMccmChainId = async (): Promise<number> =>
  pharmacyChainApi
    .getAllChain({ appName })
    .then((chains) => {
      if (!chains[0] || !chains[0].id) {
        throw new Error("cannot get chain id");
      }

      return chains[0].id;
    })
    .catch(async (error) => {
      throw await getApiError(error);
    });

export const canMigrateMccmEmsData = ({ patient, chainId }: { patient: Patient; chainId?: number }): boolean => {
  const externalIdsV2 = getMccmExternalIdsV2(patient);

  return Boolean(externalIdsV2 && chainId && externalIdsV2[chainId] && externalIdsV2[chainId].ems_user_id);
};

export const getIsAppUserIdOld = async ({
  chainId,
  patient,
  appUserId,
}: {
  chainId: number;
  patient: Patient;
  appUserId: string;
}): Promise<boolean> => {
  const baseExternalIdsV2 = getMccmExternalIdsV2(patient);

  const externalIdsV2InChain = baseExternalIdsV2?.[chainId];
  return externalIdsV2InChain?.current_app_user_id !== appUserId;
};

export const updatePatientWithMccmAppUserId = async (patient: Patient, appUserId: string): Promise<Patient> => {
  const updatingPatient = startUpdatingPatient();
  const chainId = await getMccmChainId();
  const isAppUserIdOld = await getIsAppUserIdOld({ chainId, patient, appUserId });

  if (!isAppUserIdOld) return patient;
  // チェーンの取得中に切り替わった場合は、前の患者の外部 ID を次の患者のトークンで書き込まない
  if (!isSamePatient(updatingPatient)) return patient;

  const baseExternalIdsV2 = getMccmExternalIdsV2(patient);
  const externalIdsV2InChain = baseExternalIdsV2?.[chainId];
  const newExternalIdsV2InChain = { ...externalIdsV2InChain, current_app_user_id: appUserId };
  const externalIdsV2 = { ...baseExternalIdsV2, [chainId]: newExternalIdsV2InChain };

  const updatedPatient = await patientApi
    .updateCurrentPatient({
      patchPatientRequestBody: {
        externalIdsV2,
      },
    })
    .catch(async (errorOrResponse) => {
      throw await getApiError(errorOrResponse);
    });

  // 更新中にサインアウトや別の患者への切り替えが起きた場合は、前の患者の情報を書き込まない
  if (isSamePatient(updatingPatient)) setCurrentUser(updatedPatient);

  return updatedPatient;
};
